/**
 * `@capacitor/app`: lifecycle, the Android back button, deep links.
 */
import {
  isNative,
  noopUnsubscribe,
  toUnsubscribe,
  type Unsubscribe,
} from "./platform";

export interface BackButtonEvent {
  /** Whether the WebView's own history can go back. */
  readonly canGoBack: boolean;
}

/**
 * Handles the Android hardware back button.
 *
 * **Registering any handler replaces the default**, which is to close the
 * app on the first press. So the handler owns the whole decision: navigate
 * back, close a sheet, or call `exitApp()` itself. Web: a no-op — the browser
 * has its own back button and it never reaches here.
 */
export function onBackButton(
  handler: (event: BackButtonEvent) => void
): Unsubscribe {
  if (!isNative()) {
    return noopUnsubscribe;
  }
  return toUnsubscribe(
    import("@capacitor/app").then(({ App }) =>
      App.addListener("backButton", (event) =>
        handler({ canGoBack: event.canGoBack })
      )
    )
  );
}

/**
 * Foreground/background. Native: the app's state. Web: `visibilitychange`,
 * which is the same signal for a tab.
 */
export function onAppActiveChange(
  handler: (isActive: boolean) => void
): Unsubscribe {
  if (!isNative()) {
    const listener = () => handler(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", listener);
    return () => document.removeEventListener("visibilitychange", listener);
  }
  return toUnsubscribe(
    import("@capacitor/app").then(({ App }) =>
      App.addListener("appStateChange", (state) => handler(state.isActive))
    )
  );
}

/**
 * A deep link opened the app while it was ALREADY RUNNING. Web: a no-op — a
 * link on the web is a page load, and the page reads `location` itself.
 *
 * Android fires `appUrlOpen` from `handleOnNewIntent` only, so a link that
 * cold-starts the process never reaches here: read {@link getLaunchUrl} too.
 */
export function onDeepLink(handler: (url: string) => void): Unsubscribe {
  if (!isNative()) {
    return noopUnsubscribe;
  }
  return toUnsubscribe(
    import("@capacitor/app").then(({ App }) =>
      App.addListener("appUrlOpen", (event) => handler(event.url))
    )
  );
}

/**
 * The link that COLD-STARTED the app, or `null` when a launcher tap did.
 * Web: `null` — the page reads `location` itself.
 *
 * It is the other half of {@link onDeepLink}, which fires for a warm start
 * only. The value does not change for the life of the process, so read it
 * ONCE per page load — from a React effect that can re-run, a re-mount
 * replays the link. (After an OTA apply the updater reloads the page before
 * the first render; the reloaded page reading it again is correct, because
 * the first page never rendered.)
 */
export async function getLaunchUrl(): Promise<string | null> {
  if (!isNative()) {
    return null;
  }
  const { App } = await import("@capacitor/app");
  const launch = await App.getLaunchUrl();
  return launch?.url ?? null;
}

/** Closes the app. Android only; the web cannot close its own tab. */
export async function exitApp(): Promise<void> {
  if (!isNative()) {
    return;
  }
  const { App } = await import("@capacitor/app");
  await App.exitApp();
}

export interface AppInfo {
  readonly build: string;
  readonly id: string;
  readonly name: string;
  readonly version: string;
}

/** The installed package's identity. Web: `null`, there is no package. */
export async function getAppInfo(): Promise<AppInfo | null> {
  if (!isNative()) {
    return null;
  }
  const { App } = await import("@capacitor/app");
  const info = await App.getInfo();
  return {
    build: info.build,
    id: info.id,
    name: info.name,
    version: info.version,
  };
}
